<template>
    <div>
        <div class="s-statistics__results__head">
            <h2 class="s-statistics__results__title">
                <template v-if="activeTab === 'byYear'">{{ appData.dictionary.byYearResultsTitle }}</template>
                <template v-else>
                    {{ appData.dictionary.byMonthResultsTitle }}
                    <template
                        v-if="
                            (activeTab === 'byMonth' || activeTab === 'byMonthMO') &&
                            results.byMonthYearsPeriod?.length === 2
                        ">
                        {{ results.byMonthYearsPeriod[0] }} - {{ results.byMonthYearsPeriod[1] }}
                    </template>
                </template>
            </h2>
            <template v-if="monthlyView">
                <div class="s-statistics__results__title-note">
                    <template v-if="activeTab === 'byMonth'">{{ appData.dictionary.absoluteValues }}</template>
                    <template v-if="activeTab === 'byMonthMO'">{{ appData.dictionary.averageValues }}</template>
                </div>
            </template>
        </div>
        <div class="s-statistics__table">
            <div v-if="monthlyView" class="s-statistics__tabs-nav">
                <a
                    v-for="(year, i) in monthlyViewYears"
                    :key="'table-tab-' + i"
                    href="javascript:void(0)"
                    :class="['dotted-link', { active: activeTableYear == year }]"
                    @click="activeTableYear = year">
                    {{ year }}
                </a>
            </div>

            <article class="b-article">
                <table>
                    <thead>
                        <tr>
                            <th>
                                <span v-if="monthlyView">{{ appData.dictionary.tableMonth }}</span>
                                <span v-else>{{ appData.dictionary.tableYear }}</span>
                                <br />
                                &nbsp;
                            </th>
                            <th>
                                <span>{{ appData.dictionary?.tableKatax }}</span>
                                <br />
                                <span class="green-bold">{{ appData.dictionary?.tableCount }}</span>
                            </th>
                            <th>
                                <br />
                                <span class="green-bold">
                                    {{ appData.dictionary?.tableValues }}
                                    <a href="#btmNotes">({{ appData.dictionary?.tableValuesNoteIndex }})</a>
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-if="monthlyView">
                            <tr v-for="(row, row_i) in tableMonthRows" :key="'row-' + row_i">
                                <td style="font-weight: 700">
                                    <template v-if="row.month !== -1">
                                        {{ appData.dictionary?.months[row.month] }}
                                    </template>
                                    <template v-else>{{ appData.dictionary.tableYearSum }}</template>
                                </td>
                                <td :style="{ fontWeight: row.month === -1 ? 700 : 400 }">
                                    {{ row.katax?.count?.toLocaleString(appData.configuration.locale) }}
                                </td>
                                <td :style="{ fontWeight: row.month === -1 ? 700 : 400 }">
                                    {{ row.katax?.value?.toLocaleString(appData.configuration.locale) }}
                                </td>
                            </tr>
                        </template>
                        <template v-if="!monthlyView">
                            <tr v-for="(row, row_i) in results.byYear" :key="'row-' + row_i">
                                <td>
                                    <strong>{{ row.year }}</strong>
                                </td>
                                <td>{{ row.katax?.count?.toLocaleString(appData.configuration.locale) }}</td>
                                <td>{{ row.katax?.value?.toLocaleString(appData.configuration.locale) }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </article>
        </div>

        <div v-if="chart" class="s-statistics__charts">
            <h3 class="s-statistics__charts__title">
                {{ categoryName }}
                <template v-if="activeTab === 'byMonthMO'">{{ appData.dictionary?.chartAverageTitle }}</template>
            </h3>

            <div class="s-statistics__charts__items">
                <div class="s-statistics__charts__item">
                    <div class="s-statistics__chart">
                        <line-chart
                            :key="chartUpdateKey"
                            :labels="chart.labels"
                            :datasets="chart.datasets"
                            :locale="appData.configuration.locale"
                            :is-amount="true"></line-chart>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import statisticsMixins from 'Root/vue/mixins/statisticsMixins'
import LineChart from 'Root/vue/components/LineChart.vue'
import ScrollableTable from 'Root/ui/components/ScrollableTable.js'

export default {
    components: {
        LineChart,
    },

    mixins: [statisticsMixins],

    props: {
        results: {
            type: Object,
            default: () => {
                return {}
            },
        },
        activeTab: {
            type: String,
            default: '',
        },
        category: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            activeChartTab: '',
            activeTableYear: '',

            monthlyViewYears: [],

            chart: null,
            chartUpdateKey: 0,
        }
    },

    computed: {
        monthlyView() {
            return this.activeTab === 'byMonth' || this.activeTab === 'byMonthMO'
        },
        categoryName() {
            return this.appData.filters.categories.find((i) => i.value === this.category)?.text
        },
        tableMonthRows() {
            return this.results[this.activeTab].filter((i) => i.year === this.activeTableYear)
        },
    },

    watch: {
        activeTab() {
            this.iniTableMonthsData()
            this.iniChartData()

            this.chartUpdateKey++
        },
    },

    mounted() {
        this.iniTableMonthsData()
        this.iniChartData()

        document.querySelectorAll('.b-article table').forEach((elem) => {
            elem.ScrollableTable = new ScrollableTable(elem)
        })
    },

    methods: {
        iniTableMonthsData() {
            if (this.monthlyView) {
                this.monthlyViewYears = []

                this.results[this.activeTab].forEach((resultItem) => {
                    if (!this.monthlyViewYears.includes(resultItem.year)) {
                        this.monthlyViewYears.push(resultItem.year)
                    }
                })

                this.activeTableYear = this.monthlyViewYears[0]
            }
        },
        iniChartData() {
            const labels = this.monthlyView
                ? this.createChartMonthsLabels(this.results[this.activeTab])
                : this.results[this.activeTab].map((i) => i.year)

            this.chart = {
                labels: labels,
                datasets: [
                    {
                        label: this.appData.dictionary.chartCounts,
                        data: this.results[this.activeTab].map((i) => i.katax?.count),
                    },
                    {
                        label: this.appData.dictionary.chartValues,
                        data: this.results[this.activeTab].map((i) => i.katax?.value),
                        yAxisID: 'rightY',
                    },
                ],
            }
        },
    },
}
</script>
